import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '@/store/AppContext';
import apiClient from '@/api/apiClient';
import { GeneratedImage } from '@/types';
import InfoPanel from './InfoPanel';
import DeleteConfirmModal from './DeleteConfirmModal';
import Snackbar from './Snackbar';
import { useTranslation } from '@/i18n/useTranslation';
import { useGalleryData } from './gallery/useGalleryData';
import { resolveImageUrl } from './gallery/utils';
import { buildRegenerateUrlParams } from './gallery/regenerateHelpers';
import { GalleryLoadingState, GalleryNoImagesState, GalleryNotFoundState } from './gallery/GalleryEmptyState';
import GallerySingleView from './gallery/GallerySingleView';
import GalleryGridView from './gallery/GalleryGridView';
import GalleryFilmstrip from './gallery/GalleryFilmstrip';
import MobileInfoDrawer from './gallery/MobileInfoDrawer';
import { ChevronLeft, Maximize2, Grid3x3 } from 'lucide-react';

export default function Gallery() {
  const { uuid, locale = 'en' } = useParams<{ uuid?: string; locale?: string }>();
  const navigate = useNavigate();
  const browsePath = `/${locale}/browse`;
  const createPath = `/${locale}/create`;
  const location = useLocation();
  const { setAllImages, galleryViewMode, setGalleryViewMode, isAuthenticated } = useApp();
  const { t } = useTranslation();

  const {
    images: galleryImages,
    currentImageIndex: galleryCurrentIndex,
    loadingExisting,
    totalCount,
    loadingMore,
    infoPanelVisible,
    setInfoPanelVisible,
    isDesktop,
    filmstripRef,
    thumbnailRefs,
    handleImageSelect,
    handleLoadMore,
    navigatePrevious,
    navigateNext,
  } = useGalleryData();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<GeneratedImage | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const [mobileInfoOpen, setMobileInfoOpen] = useState(false);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  useEffect(() => {
    const forceSingle = Boolean((location.state as { forceSingle?: boolean } | null)?.forceSingle);
    if (!forceSingle) return;
    if (galleryViewMode !== 'single') {
      setGalleryViewMode('single');
    }
    navigate(`${location.pathname}${location.search}${location.hash}`, { replace: true, state: null });
  }, [location, galleryViewMode, navigate, setGalleryViewMode]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        navigatePrevious();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        navigateNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigatePrevious, navigateNext]);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isDesktop) return;
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDesktop) return;
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = useCallback(() => {
    if (!isDesktop) {
      touchStartX.current = 0;
      touchEndX.current = 0;
      return;
    }
    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;
    if (Math.abs(distance) > 50) {
      distance > 0 ? navigateNext() : navigatePrevious();
    }
    touchStartX.current = 0;
    touchEndX.current = 0;
  }, [isDesktop, navigateNext, navigatePrevious]);

  const handleDeleteClick = (image: GeneratedImage, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setImageToDelete(image);
    setDeleteModalOpen(true);
  };

  const handleRegenerate = async (image: GeneratedImage, e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      const imgParams = image.params || {};
      const isPackshot =
        imgParams.mockup === 'product_packshot' || imgParams.sceneType === 'product_packshot';
      const isInfographic = imgParams.imageStyle === 'infographic';
      const generatedImageId = imgParams.generatedImageId;
      const urlParamsObj = buildRegenerateUrlParams(image);

      // Handle images with Directus IDs (preferred method)
      if (generatedImageId) {
        if (isPackshot) {
          urlParamsObj.set('productImageId', generatedImageId);
          navigate(`${createPath}?${urlParamsObj.toString()}`);
          return;
        } else if (isInfographic) {
          // For infographics, we need both referenceImageId and contentImageId
          const hasRefId = imgParams.referenceImageId;
          const hasContentId = imgParams.contentImageId;
          
          // If we have Directus IDs for images, use them
          if (hasRefId || hasContentId) {
            if (hasRefId) {
              urlParamsObj.set('referenceImageId', hasRefId);
            }
            if (hasContentId) {
              urlParamsObj.set('contentImageId', hasContentId);
            }
            navigate(`${createPath}?${urlParamsObj.toString()}`);
            return;
          }
          // If no Directus IDs, fall through to fetch from URLs (old images)
        } else {
          urlParamsObj.set('referenceImageId', generatedImageId);
          navigate(`${createPath}?${urlParamsObj.toString()}`);
          return;
        }
      }

      // Handle infographics with both reference and content images
      if (isInfographic) {
        const refImageUrl = imgParams.referenceImageUrl;
        const contentImageUrl = imgParams.contentImageUrl;
        
        // Helper function to fetch and store image
        const fetchAndStoreImage = async (url: string, storageKey: string) => {
          if (!url) return;
          
          let resolvedUrl = url;
          if (!resolvedUrl.startsWith('http') && !resolvedUrl.startsWith('data:')) {
            resolvedUrl = resolveImageUrl(resolvedUrl);
          }
          
          if (resolvedUrl.startsWith('data:')) {
            const base64 = resolvedUrl.split(',')[1];
            localStorage.setItem(storageKey, base64);
          } else {
            const response = await fetch(resolvedUrl);
            if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);
            const blob = await response.blob();
            const base64 = await new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                const result = (reader.result as string)?.split(',')[1];
                resolve(result || '');
              };
              reader.readAsDataURL(blob);
            });
            if (base64) {
              localStorage.setItem(storageKey, base64);
            }
          }
        };
        
        // Fetch both images in parallel
        await Promise.all([
          refImageUrl ? fetchAndStoreImage(refImageUrl, 'regenerate_referenceImage') : Promise.resolve(),
          contentImageUrl ? fetchAndStoreImage(contentImageUrl, 'regenerate_contentImage') : Promise.resolve()
        ]);
        
        navigate(`${createPath}?${urlParamsObj.toString()}`);
        return;
      }

      // Handle packshot and other image types
      let imageUrl = image.params?.referenceImageUrl || image.url;
      if (!imageUrl.startsWith('http') && !imageUrl.startsWith('data:')) {
        imageUrl = resolveImageUrl(imageUrl);
      }

      if (imageUrl.startsWith('data:')) {
        const base64 = imageUrl.split(',')[1];
        if (isPackshot) {
          localStorage.setItem('regenerate_productImage', base64);
          // Clear referenceImage to avoid conflicts
          localStorage.removeItem('regenerate_referenceImage');
        } else {
          localStorage.setItem('regenerate_referenceImage', base64);
          // Clear productImage to avoid conflicts
          localStorage.removeItem('regenerate_productImage');
        }
        navigate(`${createPath}?${urlParamsObj.toString()}`);
      } else {
        const response = await fetch(imageUrl);
        if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = (reader.result as string)?.split(',')[1];
          if (!base64) {
            return;
          }
          if (isPackshot) {
            localStorage.setItem('regenerate_productImage', base64);
            // Clear referenceImage to avoid conflicts
            localStorage.removeItem('regenerate_referenceImage');
          } else {
            localStorage.setItem('regenerate_referenceImage', base64);
            // Clear productImage to avoid conflicts
            localStorage.removeItem('regenerate_productImage');
          }
          navigate(`${createPath}?${urlParamsObj.toString()}`);
        };
        reader.readAsDataURL(blob);
      }
    } catch (error) {
      alert('Failed to prepare image for regeneration. Please try again.');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!imageToDelete?.id) return;
    const imageIdToDelete = imageToDelete.id;
    const newImages = galleryImages.filter((img) => img.id !== imageIdToDelete);
    setAllImages(newImages);
    setDeleteModalOpen(false);
    setImageToDelete(null);

    if (newImages.length > 0) {
      const newIndex = Math.min(galleryCurrentIndex, newImages.length - 1);
      const newImage = newImages[newIndex];
      if (newImage?.id) {
        navigate(`${browsePath}/${newImage.id}`, { replace: true });
      }
    } else {
      navigate(browsePath, { replace: true });
    }

    setSnackbarMessage(t('delete.success'));
    setSnackbarType('success');
    setSnackbarOpen(true);

    apiClient.deleteImage(imageIdToDelete).catch(() => {
      // Silent fail for background delete
    });
  };

  const handleBackFromSingle = () => {
    setGalleryViewMode('grid');
    navigate(browsePath);
  };

  if (loadingExisting) {
    return <GalleryLoadingState viewMode={galleryViewMode} isDesktop={isDesktop} infoPanelVisible={infoPanelVisible} />;
  }

  if (galleryImages.length === 0) {
    return (
      <GalleryNoImagesState
        isAuthenticated={isAuthenticated}
        onNavigateCreate={() => navigate(createPath)}
      />
    );
  }

  const currentImage =
    uuid
      ? galleryImages.find((img) => img.id === uuid) ||
        galleryImages[galleryCurrentIndex] ||
        galleryImages[0]
      : galleryImages[galleryCurrentIndex] || galleryImages[0];

  if (!currentImage) {
    return (
      <GalleryNotFoundState
        isAuthenticated={isAuthenticated}
        onNavigateCreate={() => navigate(createPath)}
      />
    );
  }

  return (
    <>
      <DeleteConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          if (!isDeleting) {
            setDeleteModalOpen(false);
            setImageToDelete(null);
          }
        }}
        onConfirm={handleDeleteConfirm}
        title={t('delete.title')}
        message={t('delete.confirm')}
        isDeleting={isDeleting}
      />

      <Snackbar
        message={snackbarMessage}
        type={snackbarType}
        isOpen={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        duration={3000}
      />

      <div className="h-full flex flex-col bg-bg-main relative overflow-hidden select-none pb-0 md:pb-0">
        {galleryViewMode === 'grid' && (
          <div className="flex-shrink-0 border-b border-border px-3 md:px-4 py-1.5 bg-bg-panel">
            <div className="flex justify-center">
              <div className="flex gap-1 bg-bg-control rounded-full p-1">
                <button
                  onClick={() => setGalleryViewMode('single')}
                  className="px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 text-text-secondary hover:text-text-primary"
                  title={t('view.single')}
                >
                  <Maximize2 className="w-3.5 h-3.5" strokeWidth={2} />
                  <span>{t('view.single')}</span>
                </button>
                <button
                  onClick={() => setGalleryViewMode('grid')}
                  className="px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 bg-accent text-bg-main"
                  title={t('view.grid')}
                >
                  <Grid3x3 className="w-3.5 h-3.5" strokeWidth={2} />
                  <span>{t('view.grid')}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {galleryViewMode === 'single' && (
          <>
            <div 
              className="flex-1 min-h-0 overflow-hidden"
              style={{
                marginRight: isDesktop && infoPanelVisible ? '300px' : '0',
                transition: 'margin-right 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                padding: '8px 0 0 0',
              }}
            >
              <div className="absolute top-3 left-3 z-[60]">
                <button
                  type="button"
                  onClick={handleBackFromSingle}
                  className="h-8 px-2 rounded-md text-xs text-text-secondary hover:text-text-primary hover:bg-bg-control transition-all inline-flex items-center gap-1.5"
                  title={t('label.back')}
                >
                  <ChevronLeft className="w-4 h-4" strokeWidth={2} />
                  <span className="text-xs">{t('label.back')}</span>
                </button>
              </div>
              <GallerySingleView
                currentImage={currentImage}
                isDesktop={isDesktop}
                infoPanelVisible={infoPanelVisible}
                onDelete={handleDeleteClick}
                onRegenerate={handleRegenerate}
                onMobileInfoOpen={() => setMobileInfoOpen(true)}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              />
            </div>

            <GalleryFilmstrip
              images={galleryImages}
              selectedIndex={galleryCurrentIndex}
              uuid={uuid}
              onSelect={handleImageSelect}
              filmstripRef={filmstripRef}
              thumbnailRefs={thumbnailRefs}
              totalCount={totalCount}
              loadingMore={loadingMore}
              onLoadMore={handleLoadMore}
              isDesktop={isDesktop}
              infoPanelVisible={infoPanelVisible}
            />
          </>
        )}

        {galleryViewMode === 'grid' && (
          <GalleryGridView
            images={galleryImages}
            uuid={uuid}
            currentImageIndex={galleryCurrentIndex}
            onSelect={handleImageSelect}
            onDelete={handleDeleteClick}
            totalCount={totalCount}
            loadingMore={loadingMore}
            onLoadMore={handleLoadMore}
            isDesktop={isDesktop}
            infoPanelVisible={infoPanelVisible}
            isAuthenticated={isAuthenticated}
        onNavigateCreate={() => navigate(createPath)}
        onOpenSession={(sessionId, imageStyle) => navigate(`${createPath}/${sessionId}?imageStyle=${encodeURIComponent(imageStyle || 'photo')}`)}
          />
        )}

        <InfoPanel
          image={currentImage}
          isVisible={infoPanelVisible}
          onToggle={() => {
            setInfoPanelVisible((prev) => {
              const newValue = !prev;
              localStorage.setItem('infoPanelVisible', newValue.toString());
              window.dispatchEvent(new CustomEvent('infoPanelToggle'));
              return newValue;
            });
          }}
          onRegenerate={handleRegenerate}
          onDelete={(image) => handleDeleteClick(image, undefined)}
          onImageUpdate={(imageId, updates) => {
            // Update local state
            const updatedImages = galleryImages.map(img =>
              img.id === imageId ? { ...img, ...updates } : img
            );
            setAllImages(updatedImages);
          }}
        />

        <MobileInfoDrawer
          isOpen={mobileInfoOpen}
          image={currentImage}
          onClose={() => setMobileInfoOpen(false)}
        />
      </div>
    </>
  );
}
